import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import { Lock, LogOut, Loader2, RefreshCw, PackageOpen, Pencil, Trash2, Check, X, StickyNote } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "ucure_admin_token";

const STATUSES = [
  { v: "pending", label: "قيد الانتظار", cls: "border-amber-300 bg-amber-50 text-amber-700" },
  { v: "confirmed", label: "مؤكد", cls: "border-emerald-300 bg-emerald-50 text-emerald-700" },
  { v: "delivered", label: "تم التوصيل", cls: "border-[#1F7A52]/40 bg-[#E8F5EF] text-[#1F7A52]" },
  { v: "cancelled", label: "ملغي", cls: "border-[#E65A6B]/40 bg-[#E65A6B]/10 text-[#E65A6B]" },
];
const statusMeta = (v) => STATUSES.find((s) => s.v === v) || STATUSES[0];

const fmtDate = (iso) =>
  new Date(iso).toLocaleString("ar", { dateStyle: "medium", timeStyle: "short" });

const inputCls =
  "w-full rounded-lg border border-[#2E9E6B]/25 bg-white px-3 py-2 text-base text-[#0F1E19] outline-none transition-all focus:border-[#1F7A52] focus:ring-2 focus:ring-[#2E9E6B]/20";

export default function AdminOrders() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [orders, setOrders] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", phone: "", city: "", address: "", notes: "" });
  const [deletingId, setDeletingId] = useState(null);

  const auth = (t) => ({ headers: { Authorization: `Bearer ${t}` } });

  const load = useCallback(async (t) => {
    setBusy(true);
    try {
      const { data } = await axios.get(`${API}/orders`, auth(t));
      setOrders(data);
      setError("");
    } catch {
      sessionStorage.removeItem(TOKEN_KEY);
      setToken("");
      setOrders(null);
      setError("انتهت الجلسة — المرجو تسجيل الدخول من جديد");
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      sessionStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      setPassword("");
    } catch {
      setError("كلمة المرور غير صحيحة");
    } finally {
      setBusy(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken("");
    setOrders(null);
  };

  const startEdit = (o) => {
    setEditingId(o.id);
    setDeletingId(null);
    setEditForm({ name: o.name, phone: o.phone, city: o.city, address: o.address || "", notes: o.notes || "" });
  };

  const saveEdit = async (id) => {
    try {
      const payload = { ...editForm };
      if (!payload.address) delete payload.address;
      const { data } = await axios.patch(`${API}/orders/${id}`, payload, auth(token));
      setOrders((os) => os.map((o) => (o.id === id ? data : o)));
      setEditingId(null);
      toast.success("تم حفظ التعديلات");
    } catch {
      toast.error("تعذّر الحفظ — تحقق من المعلومات");
    }
  };

  const changeStatus = async (id, status) => {
    try {
      const { data } = await axios.patch(`${API}/orders/${id}`, { status }, auth(token));
      setOrders((os) => os.map((o) => (o.id === id ? data : o)));
      toast.success("تم تحديث حالة الطلب");
    } catch {
      toast.error("تعذّر تحديث الحالة");
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/orders/${id}`, auth(token));
      setOrders((os) => os.filter((o) => o.id !== id));
      setDeletingId(null);
      toast.success("تم حذف الطلب");
    } catch {
      toast.error("تعذّر حذف الطلب");
    }
  };

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center px-4" data-testid="admin-login-page">
        <div className="w-full max-w-sm rounded-3xl border border-[#2E9E6B]/20 bg-white p-8 shadow-xl shadow-emerald-900/10">
          <div className="flex justify-center"><Logo /></div>
          <h1 className="font-display mt-6 text-center text-xl font-black text-[#0F1E19]">لوحة الطلبات</h1>
          <p className="mt-1 text-center text-sm text-[#566B63]">منطقة خاصة بالإدارة فقط</p>
          <form onSubmit={login} className="mt-6 space-y-4">
            <div>
              <label htmlFor="admin-password" className="mb-1.5 block text-xs font-bold text-[#0F1E19]">
                كلمة المرور
              </label>
              <input
                id="admin-password"
                data-testid="admin-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-xl border border-[#2E9E6B]/25 bg-white px-4 py-3.5 text-base text-[#0F1E19] outline-none transition-all focus:border-[#1F7A52] focus:ring-2 focus:ring-[#2E9E6B]/20"
                placeholder="••••••••••••"
                autoFocus
              />
            </div>
            {error && (
              <p className="rounded-xl bg-[#E65A6B]/10 px-4 py-2.5 text-center text-xs font-bold text-[#E65A6B]" data-testid="admin-login-error">
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={busy}
              data-testid="admin-login-button"
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#1F7A52] py-3.5 text-sm font-black text-[#FBF9F4] transition-all hover:bg-[#0F1E19] disabled:opacity-60"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
              دخول
            </button>
          </form>
          <a href="/" data-testid="admin-back-home-link" className="mt-5 block text-center text-xs font-bold text-[#1F7A52] hover:underline">
            ← العودة إلى الصفحة الرئيسية
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto min-h-screen max-w-7xl px-4 py-8 sm:px-6" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <Logo />
        <div className="flex items-center gap-3">
          <button
            onClick={() => load(token)}
            disabled={busy}
            data-testid="admin-refresh-button"
            className="flex items-center gap-2 rounded-full border border-[#2E9E6B]/30 px-4 py-2 text-xs font-bold text-[#1F7A52] transition-colors hover:bg-[#E8F5EF] disabled:opacity-60"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} />
            تحديث
          </button>
          <button
            onClick={logout}
            data-testid="admin-logout-button"
            className="flex items-center gap-2 rounded-full bg-[#0F1E19] px-4 py-2 text-xs font-bold text-[#FBF9F4] transition-colors hover:bg-[#E65A6B]"
          >
            <LogOut className="h-3.5 w-3.5" />
            خروج
          </button>
        </div>
      </div>

      <div className="mt-8 flex items-end justify-between">
        <div>
          <h1 className="font-display text-2xl font-black text-[#0F1E19] sm:text-3xl">الطلبات الواردة</h1>
          <p className="mt-1 text-sm text-[#566B63]" data-testid="admin-orders-count">
            {orders ? `${orders.length} طلب` : "جارٍ التحميل…"}
          </p>
        </div>
      </div>

      <div className="mt-6 overflow-x-auto rounded-3xl border border-[#2E9E6B]/15 bg-white shadow-lg shadow-emerald-900/5">
        {orders && orders.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-16 text-[#566B63]" data-testid="admin-empty-state">
            <PackageOpen className="h-10 w-10 text-[#2E9E6B]/40" />
            <p className="text-sm font-bold">لا توجد طلبات بعد</p>
          </div>
        ) : (
          <table className="w-full min-w-[1120px] text-right text-sm" data-testid="admin-orders-table">
            <thead>
              <tr className="border-b border-[#2E9E6B]/15 bg-[#E8F5EF]/60 text-xs font-black text-[#1F7A52]">
                <th className="px-4 py-4">رقم الطلب</th>
                <th className="px-4 py-4">الاسم</th>
                <th className="px-4 py-4">الهاتف</th>
                <th className="px-4 py-4">المدينة</th>
                <th className="px-4 py-4">العنوان</th>
                <th className="px-4 py-4">الكمية</th>
                <th className="px-4 py-4">المجموع</th>
                <th className="px-4 py-4">الحالة</th>
                <th className="px-4 py-4">التاريخ</th>
                <th className="px-4 py-4">إجراءات</th>
              </tr>
            </thead>
            <tbody>
              {(orders || []).map((o) => (
                editingId === o.id ? (
                  <tr key={o.id} className="border-b border-[#2E9E6B]/10 bg-[#FBF9F4]" data-testid={`admin-edit-row-${o.order_number}`}>
                    <td className="px-4 py-4 font-black text-[#1F7A52]" dir="ltr">{o.order_number}</td>
                    <td colSpan={9} className="px-4 py-4">
                      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                        <input aria-label="الاسم" data-testid="edit-name-input" className={inputCls} value={editForm.name} onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))} placeholder="الاسم الكامل" />
                        <input aria-label="الهاتف" data-testid="edit-phone-input" className={inputCls} dir="ltr" value={editForm.phone} onChange={(e) => setEditForm((f) => ({ ...f, phone: e.target.value }))} placeholder="06XXXXXXXX" />
                        <input aria-label="المدينة" data-testid="edit-city-input" className={inputCls} value={editForm.city} onChange={(e) => setEditForm((f) => ({ ...f, city: e.target.value }))} placeholder="المدينة" />
                        <input aria-label="العنوان" data-testid="edit-address-input" className={inputCls} value={editForm.address} onChange={(e) => setEditForm((f) => ({ ...f, address: e.target.value }))} placeholder="العنوان الكامل" />
                      </div>
                      <textarea
                        aria-label="ملاحظات"
                        data-testid="order-notes-input"
                        className={`${inputCls} mt-3 min-h-[70px] resize-y`}
                        value={editForm.notes}
                        onChange={(e) => setEditForm((f) => ({ ...f, notes: e.target.value }))}
                        placeholder="ملاحظات حول الطلب (وقت الاتصال المفضل…)"
                      />
                      <div className="mt-3 flex gap-2">
                        <button onClick={() => saveEdit(o.id)} data-testid="order-edit-save-button" className="flex items-center gap-1.5 rounded-full bg-[#1F7A52] px-5 py-2 text-xs font-black text-white transition-colors hover:bg-[#0F1E19]">
                          <Check className="h-3.5 w-3.5" /> حفظ
                        </button>
                        <button onClick={() => setEditingId(null)} data-testid="order-edit-cancel-button" className="flex items-center gap-1.5 rounded-full border border-[#2E9E6B]/30 px-5 py-2 text-xs font-bold text-[#566B63] transition-colors hover:bg-white">
                          <X className="h-3.5 w-3.5" /> إلغاء
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  <tr key={o.id} className="border-b border-[#2E9E6B]/10 transition-colors last:border-0 hover:bg-[#FBF9F4]" data-testid={`admin-order-row-${o.order_number}`}>
                    <td className="px-4 py-4 font-black text-[#1F7A52]" dir="ltr">{o.order_number}</td>
                    <td className="px-4 py-4 font-bold text-[#0F1E19]">{o.name}</td>
                    <td className="px-4 py-4" dir="ltr">{o.phone}</td>
                    <td className="px-4 py-4">{o.city}</td>
                    <td className="px-4 py-4">
                      <span className="block max-w-[180px] truncate text-xs text-[#162822]" title={o.address || ""} data-testid={`order-address-${o.order_number}`}>
                        {o.address || "—"}
                      </span>
                    </td>
                    <td className="px-4 py-4">{o.quantity}</td>
                    <td className="px-4 py-4 font-black text-[#0F1E19]">{o.total} درهم</td>
                    <td className="px-4 py-4">
                      <select
                        aria-label="حالة الطلب"
                        data-testid={`status-select-${o.order_number}`}
                        value={o.status}
                        onChange={(e) => changeStatus(o.id, e.target.value)}
                        className={`cursor-pointer rounded-full border px-3 py-1.5 text-xs font-black outline-none transition-colors ${statusMeta(o.status).cls}`}
                      >
                        {STATUSES.map((s) => (
                          <option key={s.v} value={s.v}>{s.label}</option>
                        ))}
                      </select>
                      {o.notes && (
                        <p className="mt-1.5 flex max-w-[180px] items-center gap-1 truncate text-[11px] text-[#566B63]" title={o.notes} data-testid={`order-note-${o.order_number}`}>
                          <StickyNote className="h-3 w-3 shrink-0 text-[#D4AF37]" />
                          {o.notes}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-4 text-xs text-[#566B63]">{fmtDate(o.created_at)}</td>
                    <td className="px-4 py-4">
                      {deletingId === o.id ? (
                        <div className="flex items-center gap-1.5">
                          <button onClick={() => remove(o.id)} data-testid={`confirm-delete-${o.order_number}`} className="rounded-full bg-[#E65A6B] px-3 py-1.5 text-[11px] font-black text-white transition-colors hover:bg-[#c94455]">
                            تأكيد الحذف
                          </button>
                          <button onClick={() => setDeletingId(null)} data-testid={`cancel-delete-${o.order_number}`} className="rounded-full border border-[#2E9E6B]/30 px-3 py-1.5 text-[11px] font-bold text-[#566B63] hover:bg-white">
                            إلغاء
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5">
                          <button onClick={() => startEdit(o)} data-testid={`edit-order-${o.order_number}`} aria-label="تعديل الطلب" className="flex h-8 w-8 items-center justify-center rounded-full border border-[#2E9E6B]/30 text-[#1F7A52] transition-colors hover:bg-[#E8F5EF]">
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                          <button onClick={() => { setDeletingId(o.id); setEditingId(null); }} data-testid={`delete-order-${o.order_number}`} aria-label="حذف الطلب" className="flex h-8 w-8 items-center justify-center rounded-full border border-[#E65A6B]/30 text-[#E65A6B] transition-colors hover:bg-[#E65A6B]/10">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                )
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
